import { Injectable }                        from '@angular/core';
import { AbstractBaseService }               from '@app/core/bases/services/abstractBaseService';
import { BusinessPremisesApiService }        from '@app/core/http/business-premises/business-premises-api.service';
import {
	IBusinessPremisesHttpParams,
	RequestCompleteEntityState,
	RequestCompleteListState,
	RequestState
}                                            from '@app/core/interfaces/api.interface';
import {
	BUSINESS_PREMISES_METADATA,
	IBusinessPremisesMetadata
}                                            from '@app/modules/business-premises/config/business-premises.config';
import {
	BusinessPremises,
	BusinessPremisesPresetEnum,
	BusinessPremisesStatusEnum
}                                            from '@app/modules/business-premises/models/business-premises.model';
import {
	BusinessPremisesDetailPageProductTabRoute,
	BusinessPremisesDetailPageProductTabVariantRoute,
	BusinessPremisesDetailPageRoute,
	BusinessPremisesFormPageCreateRoute,
	BusinessPremisesFormPageUpdateRoute,
	BusinessPremisesListPageRoute,
	BusinessPremisesRoutesEnum,
	BusinessPremisesUsagePageRoute,
	BusinessPremsiesListPageRouteType
}                                            from '@app/modules/business-premises/routes/business-premises.routes';
import { IPropsUpdateBusinessPremisesState } from '@app/modules/business-premises/store/business-premises.actions';
import { businessPremisesStore }             from '@app/modules/business-premises/store/business-premises.store';
import { SchedulingConfig }                  from '@app/modules/configuration/models/configuration.model';
import { OrganizationRoutesEnum }            from '@app/modules/organization/routes/organization.routes';
import { ImportTypeEnum }                    from '@app/modules/structure-import/models/structure-import.model';
import { getYearMonth }                      from '@app/modules/usage/helpers/usage-month.helper';
import {
	AllowedDetailsTabEnum,
	ISelectionData,
	ObservableTypeEnum,
	ProductEnum
}                                            from '@core/interfaces/common.interfaces';
import {
	RouteBasePath,
	RouteDefinition
}                                            from '@core/interfaces/route.interface';
import {
	IStoreConfig,
	IStoreProcessingConfig,
	StoreActionsEnum
}                                            from '@core/interfaces/store.interface';
import { ResourceLocksApiService }           from '@http/resource-locks/resource-locks-api.service';
import { IHttpContextConfig }                from '@interceptors/interceptors.model';
import { EntityVariantEnum }                 from '@models/entity.model';
import { ResourceLock }                      from '@models/resource-lock.model';
import { Store }                             from '@ngrx/store';
import {
	IChangeStatusBusinessPremisesCallbackOptions,
	IStatusFlowChangeEvent
}                                            from '@shared/components/status-flow/status-flow.model';
import { StatusFlowService }                 from '@shared/components/status-flow/status-flow.service';
import { ListOperationsIdentifierEnum }      from '@shared/list-operations/model/list-operations.model';
import { actionStatus }                      from '@shared/services/toaster.service';
import { Observable }                        from 'rxjs';
import { take }                              from 'rxjs/operators';


@Injectable( {
	providedIn: 'root'
} )
export class BusinessPremisesService extends AbstractBaseService {

	private readonly _businessPremisesMetadata: IBusinessPremisesMetadata = BUSINESS_PREMISES_METADATA;

	readonly businessPremisesStatusEnum = BusinessPremisesStatusEnum;

	constructor(
		private readonly _businessPremisesApiService: BusinessPremisesApiService,
		private readonly _resourceLockApi: ResourceLocksApiService,
		private readonly _statusFlowService: StatusFlowService,
		protected readonly store$: Store
	) {
		super( store$, businessPremisesStore.selectors, businessPremisesStore.actions );
		this.logger.trace( `${ this.constructor.name }:constructor` );
	}

	// ACTIONS #################################################################################
	private deleteBusinessPremises( businessPremisesId: string, navigateToList: boolean, caller: string ): void {
		const dialogConfirmationText = this.translationService.getI18nString(
			true,
			BUSINESS_PREMISES_METADATA.i18n.businessPremisesBasepath,
			'entity.dialogForDelete.description'
		);

		this.dialogCommonService.showConfirmationDialog( dialogConfirmationText, 'delete' )
		.pipe( take( 1 ) )
		.subscribe( res => {
			this.uiService.showPageLoaderAndCountUp();
			this.delete( businessPremisesId )
			.pipe( take( 1 ) )
			.subscribe(
				( businessPremisesState: RequestCompleteEntityState<BusinessPremises> ) => {
					if ( businessPremisesState.requestState === RequestState.SUCCESS ) {
						this.storeRemoveOne<BusinessPremises>( businessPremisesId );
					}
					this.toasterService.dispatch( businessPremisesState, this._businessPremisesMetadata.entityId, actionStatus.DELETE );
					this.uiService.hidePageLoaderOrCountDown();
					if ( navigateToList ) { this.navigateToListPage(); }
				}
			);
		} );
	}

	deleteBusinessPremisesOnDetailPage( businessPremisesId: string, caller: string ): void {
		this.deleteBusinessPremises( businessPremisesId, true, caller );
	}

	deleteBusinessPremisesOnEntity( businessPremisesId: string, caller: string ): void {
		this.deleteBusinessPremises( businessPremisesId, true, caller );
	}

	private changeBusinessPremisesStatus(
		callbackOptions: IChangeStatusBusinessPremisesCallbackOptions,
		statusChangeEvent: IStatusFlowChangeEvent<BusinessPremisesStatusEnum>
	): void {
		this._statusFlowService.changeStatus(
			{
				callback: this.changeStatusCallback,
				callbackOptions,
				caller: callbackOptions?.caller,
				entity: callbackOptions?.entity,
				entityId: this._businessPremisesMetadata.entityId,
				entityService: this,
				statusChangeEvent
			}
		);
	}

	private changeStatusCallback(
		service: BusinessPremisesService,
		callbackOptions: IChangeStatusBusinessPremisesCallbackOptions
	): void {
		const {
			caller,
			entity,
			listOperationsId
		} = { ...callbackOptions };

		const businessPremises: BusinessPremises = entity as BusinessPremises;

		if ( businessPremises ) {
			if ( listOperationsId ) {
				service.uiService.showPageLoaderAndCountUp();
				service.searchWithStoredListOperationsData( {
					caller: `${ this.constructor.name }:changeBusinessPremisesStatus-> ${ caller }`,
					listOperationsId
				} )
				.pipe( take( 1 ) )
				.subscribe( () => {
					service.uiService.hidePageLoaderOrCountDown();
				} );
			} else {
				service.checkAndUpdateEntityState(
					businessPremises,
					`${ this.constructor.name }:changeBusinessPremisesStatus-> ${ caller }`
				);
			}
		}
	}

	private pageReadBusinessPremisesHandler( readReq$: Observable<RequestCompleteEntityState<BusinessPremises>> ): void {
		readReq$
		.pipe( take( 1 ) )
		.subscribe( ( businessPremisesState: RequestCompleteEntityState<BusinessPremises> ) => {
			this.uiService.hidePageLoaderOrCountDown();
			// on fail redirects to list
			if ( businessPremisesState.requestState === RequestState.FAILED ) {
				this.toasterService.dispatch( businessPremisesState, this._businessPremisesMetadata.entityId, actionStatus.READ );
				this.navigateToListPage();
			}
		} );
	}


	readBusinessPremisesOnDetailsPage( businessPremisesId: string ): Observable<RequestCompleteEntityState<BusinessPremises>> {
		this.uiService.showPageLoaderAndCountUp();
		const readReq$ = this.read( {
			id: businessPremisesId,
			withFullResponse: false,
			skipBusinessPremisesActionItemChecks: false
		} );
		this.pageReadBusinessPremisesHandler( readReq$ );

		return readReq$;
	}

	readBusinessPremisesOnFormPage( businessPremisesId: string ): void {
		this.uiService.showPageLoaderAndCountUp();
		const readReq$ = this.read( { id: businessPremisesId, withFullResponse: false, skipBusinessPremisesActionItemChecks: true } );
		this.pageReadBusinessPremisesHandler( readReq$ );
	}


	changeBusinessPremisesStatusOnDetailPage(
		businessPremises: BusinessPremises,
		caller: string,
		statusChangeEvent: IStatusFlowChangeEvent<BusinessPremisesStatusEnum>
	): void {
		this.changeBusinessPremisesStatus(
			{
				caller,
				entity: businessPremises
			},
			statusChangeEvent
		);
	}

	changeBusinessPremisesStatusOnEntity(
		businessPremises: BusinessPremises,
		caller: string,
		statusChangeEvent: IStatusFlowChangeEvent<BusinessPremisesStatusEnum>
	): void {
		this.changeBusinessPremisesStatus(
			{
				caller,
				entity: businessPremises,
				listOperationsId: ListOperationsIdentifierEnum.BUSINESS_PREMISES_LIST_PAGE
			},
			statusChangeEvent
		);
	}

	searchBusinessPremisesByOrganization(
		options: {
			caller?: string;
			listOperationsId: ListOperationsIdentifierEnum;
			organizationId: string;
			selectionData?: ISelectionData<BusinessPremises>;
			skipBusinessPremisesChecks?: boolean;
			showPlainEntitiesAndClearStoreOnEmptyList?: boolean;
		}
	): Observable<RequestCompleteListState<BusinessPremises>> {
		return this.searchByOrganizationWithStoredListOperationsData( {
			...options,
			caller:	`${ options.caller } | ${ this.constructor.name }:searchBusinessPremisesByOrganization`,
		} );
	}

	requestTssForBusinessPremisesAfterCreate( businessPremises: BusinessPremises, caller: string ): void {
		this.store$.dispatch(
			businessPremisesStore.actions.requestTssForBusinessPremisesAfterCreate(
				{
					caller,
					businessPremises
				}
			)
		);
	}

	// Modals
	showStructureImportForBusinessPremises(): void {
		this.store$.dispatch(
			businessPremisesStore.actions.structureImportForBusinessPremises(
				{
					caller: `${ this.constructor.name }:showImport`,
					importType: ImportTypeEnum.BUSINESS_PREMISES
				}
			)
		);
	}

	showStructureExportForBusinessPremises( businessPremises: BusinessPremises, caller: string ): void {
		this.store$.dispatch(
			businessPremisesStore.actions.structureExportForBusinessPremises(
				{ businessPremises, caller: `${ caller } | ${ this.constructor.name }:showStructureExportForBusinessPremises` }
			)
		);
	}

	showBusinessPremisesConfig( businessPremises: BusinessPremises, caller: string ): void {
		this.store$.dispatch(
			businessPremisesStore.actions.showBusinessPremisesConfig(
				{ businessPremises, caller: `${ caller } | ${ this.constructor.name }:showBusinessPremisesConfig` }
			)
		);
	}

	taxationReportingBulkCreate( businessPremisesList: BusinessPremises[], caller: string ): void {
		this.taxationReportingCreateForBusinessPremises( { businessPremisesList , caller } );
	}

	private taxationReportingCreateForBusinessPremises(
		options: {
			businessPremisesList: BusinessPremises[],
			caller: string,
			refreshList?: boolean
		}
	): void {
		const {
			businessPremisesList,
			caller,
			refreshList = false
		} = options;
		const dialogConfirmationText = this.translationService.getI18nString(
			true,
			BUSINESS_PREMISES_METADATA.i18n.businessPremisesBasepath,
			'entity.dialogForTaxationReportingCreate.description'
		);

		this.dialogCommonService.showConfirmationDialog( dialogConfirmationText, 'confirm' )
		.pipe( take( 1 ) )
		.subscribe( ( modalResult: string ) => {
			if ( modalResult === 'confirm' ) {
				businessPremisesList.forEach( ( businessPremises: BusinessPremises ) => this.store$.dispatch(
					businessPremisesStore.actions.taxationReportingCreateForBusinessPremises( {
						businessPremises,
						caller: `${ caller } | ${ this.constructor.name }:taxationReportingCreateForBusinessPremises`,
						refreshList
					} )
				) );
			}
		} );
	}

	taxationReportingCreateOnEntity( businessPremises: BusinessPremises, caller: string ): void {
		this.taxationReportingCreateForBusinessPremises( {
			businessPremisesList: [ businessPremises ],
			caller
		} );
	}

	taxationReportingCreateOnDetailPage( businessPremises: BusinessPremises, caller: string, refreshList?: boolean ): void {
		this.taxationReportingCreateForBusinessPremises( {
			businessPremisesList: [ businessPremises ],
			caller,
			refreshList
		} );
	}

	// CHECKS ####################################################################
	checkAndUpdateEntityState( businessPremises: BusinessPremises, caller: string ): void {
		caller += ` | ${ this.constructor.name }:checkAndUpdateEntityState`;
		this.store$.dispatch(
			businessPremisesStore.actions.checkAndUpdateEntityState( { businessPremises, caller } )
		);
	}

	// HELPER ####################################################################
	/**
	 * Maps the property `isSelected` from a selection list onto a
	 * `BusinessPremises` list wrapped inside a `RequestCompleteListState` object.
	 * Used for conditional preservation of a selection state on an entity list
	 * after e.g. a list refresh or change of the ListOps parameters.
	 * @param businessPremisesListState The request response state.
	 * @param selectionData	Object that includes the selection list
	 * @returns the response state with the mapped list, if the
	 * `keepSelectionFlag` in the `selectionData` is `true`, otherwise the
	 * unchanged response state.
	 */
	private mapSelection(
		businessPremisesListState: RequestCompleteListState<BusinessPremises>,
		selectionData: ISelectionData<BusinessPremises>
	): RequestCompleteListState<BusinessPremises> {
		if ( selectionData?.keepSelection ) {
			return {
				...businessPremisesListState,
				list: businessPremisesListState.list.map( ( businessPremises: BusinessPremises ) => {
					const isSelected = selectionData.selectedItems.find(
						( selectedTaxRep: BusinessPremises ) => selectedTaxRep.id === businessPremises.id
					)?.isSelected;

					return {
						...businessPremises,
						isSelected,
					};
				} )
			}
		}

		return businessPremisesListState;
	}

	// STORE #####################################################################
	// SELECTORS
	// UPDATERS
	updateStoreState( updateBusinessPremisesState: IPropsUpdateBusinessPremisesState ): void {
		this.store$.dispatch( businessPremisesStore.actions.updateBusinessPremisesState( updateBusinessPremisesState ) );
	}


	// NAVIGATION ################################################################
	navigateToListPage( presetId?: BusinessPremisesPresetEnum ): void {

		/*
		 * TODO --- check
		 *	Is this really necessary?
		 *  `hasSameBasePath` seems to be --always-- `false`, even though the base
		 *  path --is-- actually the same (something seems to be wrong with the
		 *  NgRx store selector `selectRouteById()` called by the RouterService).
		 */
		this.routerService.hasRouteByIdTheSameBasePath( '0', RouteBasePath.BusinessPremises )
		.pipe( take( 1 ) )
		.subscribe( ( hasSameBasePath: boolean ) => {
			this.logger.debug(`${ this.constructor.name }:navigateToListPage->hasSameBasePath`, hasSameBasePath );
			this.listOperationsService.state.listOperationsSetRestoreFlag(
				ListOperationsIdentifierEnum.BUSINESS_PREMISES_LIST_PAGE,
				hasSameBasePath,
				`${ this.constructor.name }:navigateToListPage`
			);

			const presetIdToRouteIdMap: { [ key in BusinessPremisesPresetEnum ]?: BusinessPremsiesListPageRouteType } = {
				[ BusinessPremisesPresetEnum.BUSINESS_PREMISES ]: BusinessPremisesRoutesEnum.ListPage,
				[ BusinessPremisesPresetEnum.BULK_TAXATION_REPORTING ]: BusinessPremisesRoutesEnum.ListPageBulkTaxationReporting
			}

			const routeDefinition: BusinessPremisesListPageRoute = {
				basePath: RouteBasePath.BusinessPremises,
				path: presetIdToRouteIdMap[ presetId ] ?? BusinessPremisesRoutesEnum.ListPage
			};

			this.routerService.navigateToNextRoute(
				routeDefinition,
				`${ this.constructor.name }:navigateToListPage`
			);
		} );
	}

	navigateToDetailPage( businessPremisesId: string ): void {
		if ( businessPremisesId ) {
			const routeDefinition: BusinessPremisesDetailPageRoute = {
				basePath: RouteBasePath.BusinessPremises,
				path: BusinessPremisesRoutesEnum.DetailPage,
				params: { businessPremisesId }
			};

			this.routerService.navigateToNextRoute(
				routeDefinition,
				`${ this.constructor.name }:navigateToDetailPage`,
				true,
				true
			);
		}
	}

	navigateToDetailPageProductTab(
		businessPremisesId: string,
		product: ProductEnum,
		tab: AllowedDetailsTabEnum,
		replaceLastStoredRouteWithNew = false
	): void {
		if ( businessPremisesId ) {
			const routeDefinition: BusinessPremisesDetailPageProductTabRoute = {
				basePath: RouteBasePath.BusinessPremises,
				path: BusinessPremisesRoutesEnum.DetailPageProductTab,
				params: { businessPremisesId, product, tab },
				replaceLastStoredRouteWithNew
			};

			this.routerService.navigateToNextRoute(
				routeDefinition,
				`${ this.constructor.name }:navigateToDetailPageProductTab`
			);
		}
	}

	navigateToDetailPageProductTabVariant(
		businessPremisesId: string,
		product: ProductEnum,
		tab: AllowedDetailsTabEnum,
		variant: EntityVariantEnum,
		replaceLastStoredRouteWithNew = false
	): void {
		if ( businessPremisesId ) {
			const routeDefinition: BusinessPremisesDetailPageProductTabVariantRoute = {
				basePath: RouteBasePath.BusinessPremises,
				path: BusinessPremisesRoutesEnum.DetailPageProductTabVariant,
				params: { businessPremisesId, product, tab, variant },
				replaceLastStoredRouteWithNew
			};

			this.routerService.navigateToNextRoute(
				routeDefinition,
				`${ this.constructor.name }:navigateToDetailPageProductTabVariant`
			);
		}
	}

	navigateToFormCreate(): void {
		const routeDefinition: BusinessPremisesFormPageCreateRoute = {
			basePath: RouteBasePath.BusinessPremises,
			path: BusinessPremisesRoutesEnum.FormPageCreate
		};

		this.routerService.navigateToNextRoute(
			routeDefinition,
			`${ this.constructor.name }:navigateToCreate`
		);
	}

	navigateToFormUpdate( businessPremisesId: string ): void {
		if ( businessPremisesId ) {
			const routeDefinition: BusinessPremisesFormPageUpdateRoute = {
				basePath: RouteBasePath.BusinessPremises,
				path: BusinessPremisesRoutesEnum.FormPageUpdate,
				params: { businessPremisesId }
			};

			this.routerService.navigateToNextRoute(
				routeDefinition,
				`${ this.constructor.name }:navigateToUpdate`
			);
		}
	}

	navigateToUsage( product: ProductEnum, businessPremisesId: string ): void {
		if ( businessPremisesId && product ) {
			const routeDefinition: BusinessPremisesUsagePageRoute = {
				basePath: RouteBasePath.BusinessPremises,
				path: BusinessPremisesRoutesEnum.UsagePage,
				params: {
					product,
					businessPremisesId,
					usagePeriod: getYearMonth()
				}
			};

			this.routerService.navigateToNextRoute(
				routeDefinition,
				`${ this.constructor.name }:navigateToUsage`,
				false,
				false,
				true
			);
		}
	}

	navigateBack( caller = '', shouldRestoreListOperationData = true ): void {
		this.listOperationsService.state.listOperationsSetRestoreFlag(
			ListOperationsIdentifierEnum.BUSINESS_PREMISES_LIST_PAGE,
			shouldRestoreListOperationData,
			caller
		);

		const previousRoute: RouteDefinition = this.routerService.getPreviousRoute();
		switch ( previousRoute?.path ) {
			case OrganizationRoutesEnum.DetailPageTab:
				this.listOperationsService.state.listOperationsSetRestoreFlag(
					ListOperationsIdentifierEnum.ORGANIZATION_DETAIL_PAGE_BUSINESS_PREMISES_TAB_LIST,
					shouldRestoreListOperationData,
					caller
				);
		}

		this.routerService.navigateBack(
			this.routerService.getFallbackRoute( RouteBasePath.BusinessPremises, BusinessPremisesRoutesEnum.ListPage ),
			`${ caller } | ${ this.constructor.name }:navigateBack`
		);
	}

	// API #################################################################################
	// List Requests
	list( storeConfig?: IStoreConfig ): Observable<RequestCompleteListState<BusinessPremises>> {
		const storeProcessingConfig = this.createListStoreProcessingConfig<BusinessPremises>(
			storeConfig,
			this.storeList$<BusinessPremises>(),
			StoreActionsEnum.SET_ALL
		);

		const req$ = this._businessPremisesApiService.list();
		return this.requestListThroughProxy<BusinessPremises>( req$, storeProcessingConfig );
	}


	private searchByOrganizationWithStoredListOperationsData(
		options: {
			caller?: string;
			listOperationsId: ListOperationsIdentifierEnum;
			organizationId: string;
			selectionData?: ISelectionData<BusinessPremises>;
			skipBusinessPremisesChecks?: boolean;
			showPlainEntitiesAndClearStoreOnEmptyList?: boolean;
		}
	): Observable<RequestCompleteListState<BusinessPremises>> {
		const {
			caller = '',
			listOperationsId,
			organizationId,
			selectionData,
			skipBusinessPremisesChecks = true,
			showPlainEntitiesAndClearStoreOnEmptyList = false,
		} = options;

		return this.listOperationsService.helper.requestWithStoredListOperationsData<BusinessPremises, IBusinessPremisesHttpParams>(
			( [ organizationIdArg, listOperationsArg, callerArg, selectionDataArg, skipBusinessPremisesChecksArg, showPlainEntitiesArg, ] ) =>
				this.listByOrganizationId( {
					caller: callerArg,
					params: listOperationsArg,
					parentOrgId: organizationIdArg,
					selectionData: selectionDataArg,
					skipBusinessPremisesChecks: skipBusinessPremisesChecksArg,
					showPlainEntitiesAndClearStoreOnEmptyList: showPlainEntitiesArg,
					/*
					 * prevents unnecessary store update by "request through proxy",
					 * store is updated with mapped BP list
					 */
					storeConfig: { updateStore: false, caller }
				} ),
			[ organizationId, caller, selectionData, skipBusinessPremisesChecks, showPlainEntitiesAndClearStoreOnEmptyList ],
			1,
			listOperationsId,
		);
	}

	searchWithStoredListOperationsData(
		options: {
			caller?: string;
			listOperationsId: ListOperationsIdentifierEnum;
			selectionData?: ISelectionData<BusinessPremises>;
			skipBusinessPremisesChecks?: boolean;
		}
	): Observable<RequestCompleteListState<BusinessPremises>> {
		const {
			caller = '',
			listOperationsId,
			selectionData,
			skipBusinessPremisesChecks = true
		} = options;
		return this.listOperationsService.helper.requestWithStoredListOperationsData<BusinessPremises, IBusinessPremisesHttpParams>(
			( [ listOperationsArg, callerArg, selectionDataArg, skipBusinessPremisesChecksArg ] ) =>
				this.search( {
					caller: callerArg,
					params: listOperationsArg,
					selectionData: selectionDataArg,
					skipBusinessPremisesChecks: skipBusinessPremisesChecksArg
				} ),
			[ `${ caller } | ${ this.constructor.name }:searchWithStoredListOperationsData`, selectionData, skipBusinessPremisesChecks ],
			0,
			listOperationsId
		);
	}

	// TODO before providing storeConfig param a store search selector is needed
	//  for filtering store entries by params
	search(
		options: {
			caller?: string;
			getAllPagesData?: boolean;
			httpContextConfig?: IHttpContextConfig;
			params?: IBusinessPremisesHttpParams;
			selectionData?: ISelectionData<BusinessPremises>;
			skipBusinessPremisesChecks?: boolean;
			storeConfig?: IStoreConfig
		}
	): Observable<RequestCompleteListState<BusinessPremises>> {
		const {
			caller,
			getAllPagesData = false,
			httpContextConfig,
			selectionData,
			skipBusinessPremisesChecks = true,
			// default storeConfig setup for search as long as no search selector exists
			storeConfig = {
				getFromStoreFirst: false,
				updateStore: true
			}
		} = options;

		let storeProcessingConfig = this.createListStoreProcessingConfig<BusinessPremises>(
			storeConfig,
			this.storeList$<BusinessPremises>(),
			StoreActionsEnum.SET_ALL
		);

		storeProcessingConfig = { ...storeProcessingConfig, caller: `${ caller } | ${ this.constructor.name }:search` };

		const params = this.generateParams( options.params ) as IBusinessPremisesHttpParams;
		const getAllPagesReq$ = ( [ paramsArg, httpContextConfigArg ] ) => this._businessPremisesApiService.search( paramsArg, httpContextConfigArg );
		const req$ = getAllPagesData
								 ? this.requestAllPagesData<BusinessPremises>( getAllPagesReq$, [ params, httpContextConfig ], 0 )
								 : this._businessPremisesApiService.search( params, httpContextConfig );

		const proxyReq$ = this.requestListThroughProxy<BusinessPremises>( req$, storeProcessingConfig );

		proxyReq$.pipe( take( 1 ) )
		.subscribe( ( businessPremisesListState: RequestCompleteListState<BusinessPremises> ) => {
			if ( businessPremisesListState.requestState === RequestState.SUCCESS ) {
				businessPremisesListState = this.mapSelection( businessPremisesListState, selectionData );

				if ( !skipBusinessPremisesChecks ) {
					businessPremisesListState.list.forEach( ( businessPremises: BusinessPremises ) =>
						this.checkAndUpdateEntityState( businessPremises, `${ caller } | ${ this.constructor.name }:search->response` )
					);
				} else {
					this.storeUpsertMany<BusinessPremises>(
						businessPremisesListState.list,
						`${ caller } | ${ this.constructor.name }:search->response`
					);
				}
			}
		} );

		return proxyReq$;
	}

	listByOrganizationId(
		options: {
			caller?: string;
			getAllPagesData?: boolean
			params?: IBusinessPremisesHttpParams;
			parentOrgId: string;
			selectionData?: ISelectionData<BusinessPremises>;
			skipBusinessPremisesChecks?: boolean,
			showPlainEntitiesAndClearStoreOnEmptyList?: boolean;
			storeConfig?: IStoreConfig,
		}
	): Observable<RequestCompleteListState<BusinessPremises>> {
		const {
			caller,
			getAllPagesData = false,
			parentOrgId,
			selectionData,
			skipBusinessPremisesChecks = true,
			showPlainEntitiesAndClearStoreOnEmptyList = false,
			storeConfig
		} = options

		const storeProcessingConfig = this.createListStoreProcessingConfig<BusinessPremises>(
			storeConfig,
			null,
			StoreActionsEnum.UPSERT_MANY
		);

		const params = this.generateParams( options.params ) as IBusinessPremisesHttpParams;

		const getAllPagesReq$ = ( [ parentOrgIdArg, paramsArg ] ) => this._businessPremisesApiService.listByOrganizationId( parentOrgIdArg, paramsArg );
		const req$ = getAllPagesData
								 ? this.requestAllPagesData<BusinessPremises>( getAllPagesReq$, [ parentOrgId, params ], 1 )
								 : this._businessPremisesApiService.listByOrganizationId( parentOrgId, params );

		const proxyReq$ = this.requestListThroughProxy<BusinessPremises>( req$, storeProcessingConfig );

		proxyReq$.pipe( take( 1 ) )
		.subscribe( ( businessPremisesListState: RequestCompleteListState<BusinessPremises> ) => {

			if ( businessPremisesListState.requestState === RequestState.SUCCESS ) {
			const updateStoreWithMappedList: boolean = !!selectionData || showPlainEntitiesAndClearStoreOnEmptyList;

				businessPremisesListState = this.mapSelection( businessPremisesListState, selectionData );

				if ( showPlainEntitiesAndClearStoreOnEmptyList ) {
					const mappedList: BusinessPremises[] = businessPremisesListState.list.map( ( businessPremises: BusinessPremises ) => {
						return { ...businessPremises, showPlainEntity: true };
					} );
					businessPremisesListState = {
						...businessPremisesListState,
						list: mappedList
					}
				}
				/*
				 * BP list in the store must be freshly set to get rid of BPs that do
				 * not belong to the Org (this also should ensure that
				 * `checkAndUpdateEntityState()` works correctly)
				 */
				if ( updateStoreWithMappedList ) {
					this.storeSetAll(
						businessPremisesListState.list,
						`${ caller } | ${ this.constructor.name }:listByOrganizationId->response`
					);
				}

				if ( !skipBusinessPremisesChecks ) {
					businessPremisesListState.list.forEach( ( businessPremises: BusinessPremises ) =>
						this.checkAndUpdateEntityState( businessPremises, `${ caller } | ${ this.constructor.name }:listByOrganizationId->response` )
					);
				}
			}

			if ( businessPremisesListState.requestState === RequestState.NO_RESULT && showPlainEntitiesAndClearStoreOnEmptyList ) {
				this.storeRemoveAll();
			}
		} );

		return proxyReq$;
	}

	// Entity Requests
	/**
	 * @param options.id
	 * options.withFullResponse
	 * options.storeConfig
	 * options.skipBusinessPremisesActionItemChecks true (default)
	 * options.httpContextConfig
	 */
	read(
		options: {
			id: string,
			withFullResponse?: boolean,
			storeConfig?: IStoreConfig,
			skipBusinessPremisesActionItemChecks?: boolean,
			httpContextConfig?: IHttpContextConfig
		}
	): Observable<RequestCompleteEntityState<BusinessPremises>> {
		const {
			httpContextConfig,
			id,
			skipBusinessPremisesActionItemChecks = true,
			withFullResponse
		} = options;

		let {
			storeConfig
		} = options;

		// request from store must be skipped when withFullResponse is set true
		storeConfig = {
			getFromStoreFirst: withFullResponse ? false : storeConfig?.getFromStoreFirst,
			updateStore: storeConfig?.updateStore
		};

		const storeProcessingConfig = this.createEntityStoreProcessingConfig<BusinessPremises>(
			storeConfig,
			this.storeEntityById$<BusinessPremises>( id ),
			StoreActionsEnum.UPSERT_ONE
		);

		const req$ = this._businessPremisesApiService.read( id, { httpContextConfig, withFullResponse } );
		const proxyReq$ = this.requestEntityThroughProxy<BusinessPremises>( id, req$, storeProcessingConfig );

		proxyReq$.pipe(
			take( 1 )
		)
		.subscribe( ( businessPremisesState: RequestCompleteEntityState<BusinessPremises> ) => {
			if ( businessPremisesState.requestState === RequestState.SUCCESS && !skipBusinessPremisesActionItemChecks ) {
				this.checkAndUpdateEntityState( businessPremisesState.entity, `BusinessPremises:read->response` );
			}
		} );

		return proxyReq$;
	}

	create( businessPremises: BusinessPremises, storeConfig?: IStoreConfig ): Observable<RequestCompleteEntityState<BusinessPremises>> {
		const storeProcessingConfig = this.createEntityStoreProcessingConfig<BusinessPremises>(
			storeConfig,
			null,
			StoreActionsEnum.ADD_ONE
		);

		const req$ = this._businessPremisesApiService.create( businessPremises );
		return this.requestEntityThroughProxy<BusinessPremises>( null, req$, storeProcessingConfig );
	}

	update( businessPremises: BusinessPremises, storeConfig?: IStoreConfig ): Observable<RequestCompleteEntityState<BusinessPremises>> {
		const storeProcessingConfig: IStoreProcessingConfig<BusinessPremises> = this.createEntityStoreProcessingConfig<BusinessPremises>(
			storeConfig,
			this.storeEntityById$( businessPremises.id ),
			StoreActionsEnum.UPSERT_ONE
		);

		const req$ = this._businessPremisesApiService.update( businessPremises );
		return this.requestEntityThroughProxy<BusinessPremises>( businessPremises.id, req$, storeProcessingConfig );
	}

	private delete( businessPremisesId: string, storeConfig?: IStoreConfig ): Observable<RequestCompleteEntityState<BusinessPremises>> {
		const defaultStoreConfig: IStoreConfig = {
			getFromStoreFirst: false,
			updateStore: true
		};

		const storeProcessingConfig = this.createEntityStoreProcessingConfig<BusinessPremises>(
			storeConfig ?? defaultStoreConfig,
			this.storeEntityById$( businessPremisesId ),
			StoreActionsEnum.REMOVE_ONE
		);

		const req$ = this._businessPremisesApiService.delete( businessPremisesId );
		return this.requestEntityThroughProxy<BusinessPremises>( businessPremisesId, req$, storeProcessingConfig );
	}

	private changeStatus(
		businessPremises: BusinessPremises,
		status: BusinessPremisesStatusEnum,
		storeConfig?: IStoreConfig
	): Observable<RequestCompleteEntityState<BusinessPremises>> {
		const defaultStoreConfig: IStoreConfig = {
			getFromStoreFirst: false,
			updateStore: true
		};

		const storeProcessingConfig = this.createEntityStoreProcessingConfig<BusinessPremises>(
			storeConfig ?? defaultStoreConfig,
			this.storeEntityById$( businessPremises.id ),
			StoreActionsEnum.UPSERT_ONE
		);

		// this.updateEntityRequestState(
		// 	businessPremises,
		// 	RequestState.PENDING,
		// 	`${ this.constructor.name }:changeStatus`
		// );

		const req$ = this._businessPremisesApiService.changeStatus( { ...businessPremises, status } );
		return this.requestEntityThroughProxy<BusinessPremises>( businessPremises.id, req$, storeProcessingConfig );
	}

	// Scheduling Config
	readSchedulingConfig(
		businessPremisesId: string, withFullResponse: boolean,
		observableReturnType?: ObservableTypeEnum
	): Observable<RequestCompleteEntityState<SchedulingConfig>> {
		const req$ = this._businessPremisesApiService.readSchedulingConfig( businessPremisesId, withFullResponse );
		return this.requestEntityThroughProxy<SchedulingConfig>( businessPremisesId, req$, null, observableReturnType );
	}

	createSchedulingConfig(
		businessPremisesId: string, configuration: SchedulingConfig,
		observableReturnType?: ObservableTypeEnum
	): Observable<RequestCompleteEntityState<SchedulingConfig>> {
		const req$ = this._businessPremisesApiService.createSchedulingConfig( businessPremisesId, configuration );
		return this.requestEntityThroughProxy<SchedulingConfig>( businessPremisesId, req$, null, observableReturnType );
	}

	updateSchedulingConfig(
		businessPremisesId: string, configuration: SchedulingConfig,
		observableReturnType?: ObservableTypeEnum
	): Observable<RequestCompleteEntityState<SchedulingConfig>> {

		const req$ = this._businessPremisesApiService.updateSchedulingConfig( businessPremisesId, configuration );
		return this.requestEntityThroughProxy<SchedulingConfig>( businessPremisesId, req$, null, observableReturnType );
	}

	deleteSchedulingConfig( businessPremisesId: string, observableReturnType?: ObservableTypeEnum ): Observable<RequestCompleteEntityState<SchedulingConfig>> {
		const req$ = this._businessPremisesApiService.deleteSchedulingConfig( businessPremisesId );
		return this.requestEntityThroughProxy<SchedulingConfig>( businessPremisesId, req$, null, observableReturnType );
	}

	listResourceLocks( businessPremisesId: string, withFullResponse: boolean ): Observable<RequestCompleteListState<ResourceLock>> {
		const req$ = this._resourceLockApi.getResourceLocksByBusinessPremisesId( businessPremisesId, withFullResponse );
		return this.requestListThroughProxy<ResourceLock>( req$ );
	}
}
